<script setup lang="ts">
import { Product } from '@src/product/interfaces/product.ts';
import { ref, watch } from 'vue';
import apiClient from '@src/api/api-client.ts';
import ItemLoader from '@components/loader/item-loader.vue';
import Text from '@components/text.vue';
import useProductComposable from '@src/product/composables/product-composable.ts';
import { ItemInfoProps, useItemInfoComposable } from '@components/composables/item-info-composable';
import CenterChildrenWrapper from '@components/layout-wrapper/center-children-wrapper.vue';
import Link from '@components/link.vue';
import Separator from '@components/separator.vue';
import VerticalAlignChildrenWrapper from '@components/layout-wrapper/vertical-align-children-wrapper.vue';
import Card from '@components/card.vue';
import HorizontalAlignChildrenWrapper from '@components/layout-wrapper/horizontal-align-children-wrapper.vue';
import IconTooltip from '@components/icons/icon-tooltip.vue';
import Headline from '@components/headline.vue';

defineProps<ItemInfoProps>();

const { product, loading: productLoading } = useItemInfoComposable();

const siblings = ref<Array<Product>>([]);

const loading = ref(true);
watch([product, productLoading], async () => {
    loading.value = true;
    if (productLoading.value) {
        return;
    }

    siblings.value = (await apiClient.get('/be/product/siblings/' + product.idProduct)).data;
    loading.value = false;
}, { deep: true, immediate: true });

const { getShopLink, getItemInfoLink } = useProductComposable();
</script>

<template>
    <div>
        <VerticalAlignChildrenWrapper v-if="!loading && siblings.length > 0" gap-bottom="2">
            <Separator gap="2"/>
            <Headline>Weitere Varianten ({{ siblings[0].productAbstract.varname }}):</Headline>
            <HorizontalAlignChildrenWrapper gap="1" wrap :class="$style.siblings" :wrap-on-small-screen="false">
                <Card v-for="sibling in siblings" :spread="false">
                    <HorizontalAlignChildrenWrapper gap="3" :wrap-on-small-screen="false">
                        <Text bold>{{ sibling.varselect }}</Text>
                        <HorizontalAlignChildrenWrapper gap="2" :wrap-on-small-screen="false">
                            <Link
                                :href="getItemInfoLink(sibling.sku)"
                                :force-new-tab="true"
                            >
                                <IconTooltip
                                    icon="share-square"
                                    tooltip="Link ins ZED"
                                    color="primary"
                                />
                            </Link>
                            <Link
                                v-if="sibling.productAbstract.defaultUrl"
                                :href="getShopLink(sibling.productAbstract.defaultUrl)"
                                :force-new-tab="true"
                            >
                                <IconTooltip
                                    icon="shopping-cart"
                                    tooltip="Link zum Shop"
                                    color="primary"
                                />
                            </Link>
                        </HorizontalAlignChildrenWrapper>
                    </HorizontalAlignChildrenWrapper>
                </Card>
            </HorizontalAlignChildrenWrapper>
        </VerticalAlignChildrenWrapper>
        <CenterChildrenWrapper v-if="loading">
            <ItemLoader :static-loader="true"/>
        </CenterChildrenWrapper>
    </div>
</template>

<style lang="scss" module>
.siblings {
    max-width: 70em;
}
</style>
